import { isObject, isArray } from './utils';

/**
 * Reuses object references when values are structurally equal.
 *
 * Performs deep comparison and returns the existing object reference
 * when values are structurally identical, providing referential stability
 * for React component props and preventing unnecessary re-renders.
 *
 * @note Consider using {@link withEffects} instead, which combines
 * this function and {@link resolveEffectsFromCache}.
 */
export const reuseSubtree = <T>(existing: T, fresh: T): T => {
  if (Object.is(existing, fresh)) {
    return existing;
  }

  if (isArray(existing) && isArray(fresh)) {
    if (existing.length !== fresh.length) {
      return fresh;
    }

    type TArray = T & unknown[];
    const reused = reuseAttributes(
      existing,
      fresh,
      Array.from({ length: fresh.length }, (_, i) => i),
      [] as TArray
    );

    return reused;
  }

  if (isObject(fresh) && isObject(existing)) {
    type TKey = keyof (T & Record<string, unknown>);
    type TObject = T & Record<string, unknown>;

    const existingKeys: TKey[] = Object.keys(existing);
    const freshKeys: TKey[] = Object.keys(fresh);
    if (existingKeys.length !== freshKeys.length) {
      return fresh;
    }

    return reuseAttributes(existing, fresh, freshKeys, {} as TObject);
  }

  return fresh;
};

const reuseAttributes = <T>(
  existing: T,
  fresh: T,
  freshKeys: (keyof T)[],
  attributes: T
) => {
  let allEqual = true;

  for (const key of freshKeys) {
    const existingAttribute = existing[key];
    const freshAttribute = fresh[key];
    const attribute = reuseSubtree(existingAttribute, freshAttribute);
    if (!Object.is(attribute, existingAttribute)) {
      allEqual = false;
    }
    attributes[key] = attribute;
  }

  if (allEqual) {
    return existing;
  }
  return attributes;
};
